import * as z from 'zod'

/**
 * Agent-authored chapters on a Proof claim recording. The acting agent names each group of
 * computer actions as it works; the host owns identity, sequence and media time. Three shapes
 * share this module: the agent's input on `computer_act`, the live snapshot the host publishes
 * while recording, and the completed ranges stored on the `video` evidence item.
 */

export const PROOF_CHAPTER_TITLE_MAX = 120
export const PROOF_CHAPTER_SUMMARY_MAX = 240
/** A sixth `start` is rejected before its actions run. */
export const PROOF_CHAPTERS_MAX = 5
export const PROOF_ACTION_EVENTS_MAX = 40

const ChapterText = (max: number) => z.string().trim().min(1).max(max)

export type ProofChapterOperation = z.infer<typeof ProofChapterOperation>
export const ProofChapterOperation = z.enum(['start', 'continue'])

/** Required on a `computer_act` while its attempt is recording; `continue` rewords the open chapter without moving its start. */
export type ProofChapterInput = z.infer<typeof ProofChapterInput>
export const ProofChapterInput = z.object({
  operation: ProofChapterOperation,
  title: ChapterText(PROOF_CHAPTER_TITLE_MAX),
  summary: ChapterText(PROOF_CHAPTER_SUMMARY_MAX),
})

/** `c_<recording_id>_<source_seq>`, e.g. `c_1d0270cf-8bf0-4f3e-8586-94ef5acdfb3a_3`. */
export const ProofChapterId = z.string().regex(/^c_[0-9a-f-]{36}_[1-9][0-9]*$/)

const MediaMs = z.number().int().nonnegative().max(2_147_483_647)

export type ProofRecordingActionIcon = z.infer<typeof ProofRecordingActionIcon>
export const ProofRecordingActionIcon = z.enum([
  'pointer',
  'text',
  'compass',
  'scroll',
  'resize',
  'clock',
  'hand',
  'keyboard',
  'check',
  'upload',
  'back',
  'pause',
  'circle',
])

export type ProofRecordingActionEvent = z.infer<typeof ProofRecordingActionEvent>
export const ProofRecordingActionEvent = z.object({
  id: z.string().regex(/^a_[0-9a-f-]{36}_[1-9][0-9]*$/),
  journal_seq: z.number().int().positive().max(2_147_483_647),
  at_ms: MediaMs,
  action: ChapterText(80),
  label: ChapterText(160),
  ok: z.boolean(),
  icon: ProofRecordingActionIcon,
})

const ChapterCore = z.object({
  id: ProofChapterId,
  /** Host control-event sequence of the `start` that created the chapter; never a journal seq. */
  source_seq: z.number().int().positive(),
  title: ChapterText(PROOF_CHAPTER_TITLE_MAX),
  summary: ChapterText(PROOF_CHAPTER_SUMMARY_MAX),
  start_ms: MediaMs,
})

/** A chapter while recording: the open one has no `end_ms`; closed ones end where the next starts. */
export type ProofLiveChapter = z.infer<typeof ProofLiveChapter>
export const ProofLiveChapter = ChapterCore.extend({
  end_ms: MediaMs.optional(),
  /** Actions the journal attributed to this chapter so far, failures included. */
  actions: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
})

export type ProofRecordingState = z.infer<typeof ProofRecordingState>
export const ProofRecordingState = z.enum(['recording', 'stopped', 'discarded'])

/**
 * The complete live state of one recording, published whole on every change. `revision` is the
 * host control-event sequence of the last applied event, so a reader keeps the highest revision
 * per `recording_id`. `stopped` and `discarded` are final, except that a stopped take becomes
 * `discarded` (one frame, higher revision, chapters unchanged) when a new take on the same attempt
 * supersedes its live card.
 */
export type ProofRecordingTimelineSnapshot = z.infer<typeof ProofRecordingTimelineSnapshot>
export const ProofRecordingTimelineSnapshot = z
  .object({
    recording_id: z.uuid(),
    claim_id: z.string().min(1),
    claim_attempt_id: z.uuid(),
    revision: z.number().int().positive(),
    state: ProofRecordingState,
    /** Media time of the last applied event; the measured duration once stopped. */
    elapsed_ms: MediaMs,
    chapters: z.array(ProofLiveChapter).max(PROOF_CHAPTERS_MAX),
    /** The encoder hit its size or duration cap before stop; chapters are clipped to the kept frames. */
    recording_truncated: z.literal(true).optional(),
  })
  .superRefine((snapshot, ctx) => {
    const last = snapshot.chapters.length - 1
    snapshot.chapters.forEach((chapter, index) => {
      const previous = snapshot.chapters[index - 1]
      if (previous && chapter.source_seq <= previous.source_seq) {
        ctx.addIssue({
          code: 'custom',
          message: 'source_seq must increase along the chapters',
          path: ['chapters', index, 'source_seq'],
        })
      }
      if (chapter.end_ms === undefined) {
        if (index !== last || snapshot.state === 'stopped') {
          ctx.addIssue({
            code: 'custom',
            message: 'only the last chapter of a running recording may be open',
            path: ['chapters', index, 'end_ms'],
          })
        }
      } else if (chapter.end_ms < chapter.start_ms) {
        ctx.addIssue({
          code: 'custom',
          message: 'end_ms precedes start_ms',
          path: ['chapters', index, 'end_ms'],
        })
      } else if (snapshot.state === 'stopped' && chapter.end_ms > snapshot.elapsed_ms) {
        ctx.addIssue({
          code: 'custom',
          message: 'a stopped recording has no chapter past its duration',
          path: ['chapters', index, 'end_ms'],
        })
      }
      // Closed chapters end exactly where the next starts: no overlap and no gap.
      if (previous && chapter.start_ms < (previous.end_ms ?? previous.start_ms)) {
        ctx.addIssue({
          code: 'custom',
          message: 'chapters must be ordered by start_ms and must not overlap',
          path: ['chapters', index, 'start_ms'],
        })
      } else if (previous?.end_ms !== undefined && chapter.start_ms > previous.end_ms) {
        ctx.addIssue({
          code: 'custom',
          message: 'a chapter must start where the previous one ends',
          path: ['chapters', index, 'start_ms'],
        })
      }
    })
  })

/** A chapter on the completed recording: a half-open range `[start_ms, end_ms)` inside the measured duration. */
export type ProofRecordingChapter = z.infer<typeof ProofRecordingChapter>
export const ProofRecordingChapter = ChapterCore.extend({
  end_ms: z.number().int().positive().max(2_147_483_647),
  events: z.array(ProofRecordingActionEvent).max(PROOF_ACTION_EVENTS_MAX).optional(),
})

/**
 * Ordered, non-overlapping, at most five, with strictly increasing `source_seq` (so ids are
 * unique). Empty is a valid playback state (capture off, retained video, or nothing survived
 * truncation), never an authoring shortcut for recorded actions.
 */
export type ProofRecordingChapters = z.infer<typeof ProofRecordingChapters>
export const ProofRecordingChapters = z
  .array(ProofRecordingChapter)
  .max(PROOF_CHAPTERS_MAX)
  .superRefine((chapters, ctx) => {
    let journalSeq = 0
    chapters.forEach((chapter, index) => {
      if (chapter.end_ms <= chapter.start_ms) {
        ctx.addIssue({
          code: 'custom',
          message: 'a chapter must have positive length',
          path: [index, 'end_ms'],
        })
      }
      const previous = chapters[index - 1]
      if (previous && chapter.start_ms < previous.end_ms) {
        ctx.addIssue({
          code: 'custom',
          message: 'chapters must not overlap and must be ordered by start_ms',
          path: [index, 'start_ms'],
        })
      }
      if (previous && chapter.source_seq <= previous.source_seq) {
        ctx.addIssue({
          code: 'custom',
          message: 'source_seq must increase along the chapters',
          path: [index, 'source_seq'],
        })
      }
      let atMs = chapter.start_ms
      chapter.events?.forEach((event, eventIndex) => {
        const path = [index, 'events', eventIndex]
        if (event.journal_seq <= journalSeq) {
          ctx.addIssue({
            code: 'custom',
            message: 'journal_seq must increase across all chapters',
            path: [...path, 'journal_seq'],
          })
        }
        journalSeq = event.journal_seq
        if (event.at_ms < atMs || event.at_ms >= chapter.end_ms) {
          ctx.addIssue({
            code: 'custom',
            message: 'events must be ordered inside [start_ms, end_ms)',
            path: [...path, 'at_ms'],
          })
        }
        atMs = event.at_ms
      })
    })
    if (
      chapters.reduce((count, chapter) => count + (chapter.events?.length ?? 0), 0) >
      PROOF_ACTION_EVENTS_MAX
    ) {
      ctx.addIssue({ code: 'custom', message: 'recording summaries contain at most 40 moments' })
    }
  })

/**
 * The checks that need the recording beside the chapters: ids belong to it and ranges fit its
 * measured duration. Run wherever a `video` item is accepted (stop, close_claim, readers).
 */
export function completedChaptersViolation(
  chapters: ProofRecordingChapters,
  recording: { recording_id: string; duration_ms: number }
): string | null {
  for (const chapter of chapters) {
    if (chapter.id !== proofChapterId(recording.recording_id, chapter.source_seq)) {
      return `chapter ${chapter.id} does not belong to recording ${recording.recording_id}`
    }
    if (chapter.end_ms > recording.duration_ms) {
      return `chapter ${chapter.id} ends at ${chapter.end_ms} ms, past the ${recording.duration_ms} ms recording`
    }
    for (const event of chapter.events ?? []) {
      if (event.id !== proofActionEventId(recording.recording_id, event.journal_seq)) {
        return `event ${event.id} does not belong to recording ${recording.recording_id}`
      }
    }
  }
  return null
}

export function proofChapterId(recordingId: string, sourceSeq: number): string {
  return `c_${recordingId}_${sourceSeq}`
}

export function proofActionEventId(recordingId: string, journalSeq: number): string {
  return `a_${recordingId}_${journalSeq}`
}
